import styled from '@emotion/styled';
import type { LineItem } from '@/domain/entities/order.entity';
import { formatCurrency } from './utils';

const Row = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: ${({ theme }) => theme.space.md};
  align-items: center;
  padding: ${({ theme }) => theme.space.md} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.xs}) {
    grid-template-columns: 56px 1fr;
    gap: ${({ theme }) => theme.space.sm};
  }
`;

const Image = styled.img`
  width: 72px;
  height: 90px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.background.secondary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.xs}) {
    width: 56px;
    height: 70px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Description = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Qty = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const Price = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.mediaQueries.xs}) {
    grid-column: 2;
  }
`;

interface OrderLineItemProps {
  readonly item: LineItem;
  readonly locale?: string;
  readonly currencyCode?: string;
  readonly currencySymbol?: string;
}

export function OrderLineItem({
  item,
  locale = 'en-US',
  currencyCode = 'USD',
  currencySymbol,
}: OrderLineItemProps) {
  const priceStr = formatCurrency(item.unit_price.display, locale, currencyCode, currencySymbol);
  const totalStr = formatCurrency(
    item.unit_price.display * item.quantity,
    locale,
    currencyCode,
    currencySymbol,
  );

  return (
    <Row>
      <Image
        src={item.image_url || 'https://placehold.co/72x90/f5f5f5/999?text=N/A'}
        alt={item.name}
        loading="lazy"
      />
      <Info>
        <Name>{item.name}</Name>
        {item.description && <Description>{item.description}</Description>}
        <Qty>
          Qty: {item.quantity} × {priceStr}
        </Qty>
      </Info>
      <Price>{totalStr}</Price>
    </Row>
  );
}
